import { IconButton } from '@mui/material'
import type { CSSProperties, ReactNode } from 'react'
import { useCallback, useContext } from 'react'
import {
  TabPanel as AriaTabPanel,
  Tabs as AriaTabs,
  Heading,
} from 'react-aria-components'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselineSearch from '~icons/ic/baseline-search'
import IcRoundArrowBack from '~icons/ic/round-arrow-back'
import { cx } from 'src/utils/cx'
import { UIButton } from '../ui/Button'
import { UITab, UITabList } from '../ui/Tab'
import { GenerationSelectableDialogContext } from './GenerationSelectableDialogContext'

/**
 * RAC only supports string keys
 */
interface GenerationSelectableDialogContentProps<T extends string> {
  onClose: () => void
  /**
   * Title of the dialog. Displayed at top left corner.
   */
  title: ReactNode
  /**
   * Whether to show the dialog in model sheet style.
   *
   * Commonly used on mobile view.
   */
  showModalSheet?: boolean

  /**
   * Currently selected tab
   */
  tabVal: T
  /**
   * List of available tabs to select
   */
  tabs: { id: T; label: ReactNode; content: ReactNode }[]
  /**
   * Optional suffix for tabs. Filters can be placed here.
   *
   * Position:
   *   - Normal: At the end of the row of tabs.
   *   - ModalSheet: Below the row of tabs.
   * @returns Tab Suffix for desktop view
   */
  tabsSuffix?: ReactNode

  /**
   * Optional callback when tab is changed.
   * @param event
   * @param value
   * @returns
   */
  onTabChange?: (value: T) => void

  action?: ReactNode
  searchInput: ReactNode
  preview?: ReactNode

  /**
   * Will only display when showModalSheet is true
   * Opens detail
   */
  detailActionArea?: ReactNode

  /**
   * Open Detail state handler
   * Necessary for preview in ModalSheet
   *
   * @param open
   * @returns
   */
  onOpenDetailChange?: (open: boolean) => void
  openDetail?: boolean
}

/**
 *
 * A common dialog content structure used in generation page.
 *
 * The structure is as follows:
 *
 * S: tabsSuffix
 * A: action
 * X: Close Button
 *
 * Desktop:
 * | Title | Search        |A|X|
 * |---------------------------|
 * | Tabs          S |         |
 * |                 |         |
 * |                 |         |
 * | Tab Content     | Preview |
 * |                 |         |
 * |                 |         |
 * |---------------------------|
 *
 * Mobile Pane 1        Pane 2 (Mobile Detail)
 * | Tabs     Search |  | <- Title        |
 * |-----------------|  |-----------------|
 * | S               |  |                 |
 * |                 |  |                 |
 * |   Tab Content   |  |     Preview     |
 * |                 |  |                 |
 * |-----------------|  |                 |
 * |  Open Detail    |  |                 |
 * |-----------------|  |-----------------|
 *
 * The following regions are supposed to use this component:
 *
 * - [x] Workflow LoRA Selector
 * - [x] Model
 * - [x] LoRA
 * - [x] Character
 */
export function GenerationSelectableDialogContent<T extends string>(
  props: GenerationSelectableDialogContentProps<T>,
) {
  const [formState] = useContext(GenerationSelectableDialogContext)

  const onTabChange = useCallback((value: T) => {
    props.onTabChange?.(value)
  }, [])

  const hasNoPreview = props.preview === undefined
  const shouldShowPreview = props.showModalSheet ? props.openDetail : true
  const shouldShowTabSuffix = props.showModalSheet ? !props.openDetail : true

  return (
    <>
      <div
        className="mlg:grid flex flex-col grid-cols-[1fr_288px] grid-rows-[56px_1fr] h-full"
        style={
          {
            '--list-height': !props.showModalSheet
              ? 'calc(min(100vh - 64px, 620px) - 56px - 52px)'
              : '100%',
          } as CSSProperties
        }
      >
        <div
          className={cx(
            'hidden mlg:flex items-center gap-x-2 mlg:gap-x-6',
            'mlg:pl-4 mlg:pr-2 h-14',
            'border-b border-background-light',
            'col-span-2',
          )}
        >
          <DesktopTopbar {...props} />
        </div>
        <AriaTabs
          selectedKey={props.tabVal}
          onSelectionChange={val => onTabChange(val as T)}
          orientation="horizontal"
          className={cx(
            'relative h-full flex-1 flex flex-col pt-0 px-0 pb-30 mlg:pb-0',
            hasNoPreview && 'col-span-2',
          )}
        >
          <div
            className={cx(
              'flex px-4 flex-col py-2 mlg:flex-row mlg:pt-1 gap-y-2',
            )}
          >
            <div
              className={cx(
                'border-b border-background-light flex mlg:hidden items-center gap-2 h-14',
              )}
            >
              {/* For Mobile */}
              <TabListInDialog
                tabs={props.tabs}
                className={cx(
                  'flex mr-auto',
                  (props.openDetail ||
                    formState.openSearch ||
                    !props.showModalSheet) &&
                    'hidden',
                )}
              />
              <MobileTopbar {...props} />
            </div>
            {/* For Desktop */}
            <TabListInDialog
              tabs={props.tabs}
              className="hidden mlg:flex mr-auto"
            />
            {shouldShowTabSuffix && props.tabsSuffix}
          </div>
          {props.tabs.map(tab => (
            <AriaTabPanel
              key={tab.id}
              id={tab.id}
              className={cx(
                'overflow-y-auto h-full pl-4',
                props.openDetail && 'max-mlg:hidden',
              )}
            >
              {tab.content}
            </AriaTabPanel>
          ))}
        </AriaTabs>
        {!props.openDetail && props.detailActionArea}
        {/* Hide Preview on mobile unless detail is open */}
        {shouldShowPreview && props.preview}
      </div>
    </>
  )
}

function DesktopTopbar<T extends string>(
  props: Pick<
    GenerationSelectableDialogContentProps<T>,
    'title' | 'searchInput' | 'action' | 'onClose'
  >,
) {
  return (
    <>
      <Heading slot="title" className="pl-2 text-lg font-semibold">
        {props.title}
      </Heading>

      {props.searchInput}

      <div className="flex ml-auto gap-x-2">
        {props.action}
        <IconButton color="primary" onClick={props.onClose}>
          <IcBaselineClose className="size-6" />
        </IconButton>
      </div>
    </>
  )
}

function MobileTopbar<T extends string>(
  props: Pick<
    GenerationSelectableDialogContentProps<T>,
    | 'title'
    | 'searchInput'
    | 'action'
    | 'onClose'
    | 'tabs'
    | 'openDetail'
    | 'onOpenDetailChange'
  > & { tabVal: T },
) {
  const [formState, setFormState] = useContext(
    GenerationSelectableDialogContext,
  )

  if (props.openDetail)
    return (
      <UIButton
        className="w-full justify-start h-14"
        size="large"
        variant="ghost"
        icon={<IcRoundArrowBack />}
        onPress={() => {
          props.onOpenDetailChange?.(false)
        }}
      >
        <span className="pl-1 inline-block w-full text-start">
          {props.tabs.find(tab => tab.id === props.tabVal)?.label}
        </span>
      </UIButton>
    )
  if (formState.openSearch)
    return (
      <>
        {props.searchInput}
        <IconButton
          className="text-primary-light"
          onClick={() => {
            setFormState(prev => ({
              ...prev,
              openSearch: false,
            }))
          }}
        >
          <IcBaselineClose className="size-6" />
        </IconButton>
      </>
    )
  return (
    <IconButton
      className="ml-auto text-primary-light"
      onClick={() => {
        setFormState(prev => ({
          ...prev,
          openSearch: true,
        }))
      }}
    >
      <IcBaselineSearch className="size-6" />
    </IconButton>
  )
}

function TabListInDialog<T extends string>(
  props: StyleProps & Pick<GenerationSelectableDialogContentProps<T>, 'tabs'>,
) {
  const { tabs, ...rest } = props
  return (
    <UITabList variant="underlined" {...rest}>
      {tabs.map(tab => (
        <UITab
          variant="underlined"
          key={tab.id}
          id={tab.id}
          className="mlg:px-8 px-4"
        >
          {tab.label}
        </UITab>
      ))}
    </UITabList>
  )
}
